/**
 * Llamadas HTTP del dominio Horario (scheduling).
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials: 'include'`,
 * el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request, así que la
 * rejilla y los slots se calculan para la sede activa sin que la feature toque la
 * cabecera a mano (el backend resuelve y valida la sede con `resolve_api_sede`).
 *
 * Los endpoints son **read-only**: el backend devuelve dicts JSON-safe con una
 * lista blanca de claves (`domains/scheduling.py`); aquí solo se tipa la respuesta.
 * Un `ApiError` (401/403/400 por sede/5xx) lo propaga el cliente y lo gestionan los
 * hooks/pantalla.
 *
 * Réplica de la superficie de scheduling que las pantallas necesitan (réplica del
 * contenido de `schedule.html`/`laser_schedule.html`): la rejilla semanal (normal y
 * láser) y el detalle de las órdenes de un slot (normal y láser). El horario normal
 * y el láser son endpoints simétricos; el flag `laser` elige el par correcto.
 */
import { api } from "@/lib/api";

import type { Schedule, SlotOrders } from "./types";

/**
 * Rejilla semanal de la sede activa para el desplazamiento `weekOffset`.
 * `laser=false` -> `GET /api/v1/schedule` (permiso backend `schedule`, excluye
 * láser y anuladas); `laser=true` -> `GET /api/v1/laser_schedule` (permiso
 * `laser_schedule`, solo órdenes láser). `weekOffset`: 0 = semana actual.
 */
export async function fetchSchedule(
  weekOffset: number,
  laser: boolean,
): Promise<Schedule> {
  const path = laser ? "laser_schedule" : "schedule";
  const response = await api.get(path, {
    searchParams: { week: weekOffset },
  });
  return (await response.json()) as Schedule;
}

/**
 * Órdenes de un slot (fecha + hora) de la sede activa. `laser=false` ->
 * `GET /api/v1/orders_for_slot`; `laser=true` -> `GET /api/v1/laser_orders_for_slot`
 * (ambos login-only en el backend). `date` en ISO `YYYY-MM-DD`, `time` en el formato
 * de la celda (hora 12h). Devuelve `{orders: [...]}`; fecha/hora ausentes o mal
 * formadas -> lista vacía (mismo comportamiento tolerante del service).
 */
export async function fetchSlotOrders(
  date: string,
  time: string,
  laser: boolean,
): Promise<SlotOrders> {
  const path = laser ? "laser_orders_for_slot" : "orders_for_slot";
  const response = await api.get(path, {
    searchParams: { date, time },
  });
  return (await response.json()) as SlotOrders;
}
